// src/components/Sales/SalesStatusDialog.tsx

import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { SalesStatus } from '../../types/sales';
import { useUser } from '../../hooks/useUser';
import { useAuth } from 'react-oidc-context';
import { salesService } from '../../services/salesService';

interface SalesStatusDialogProps {
    open: boolean;
    onClose: () => void;
    salesId: number;
    currentStatus: SalesStatus;
    newStatus: SalesStatus;
    onSuccess: () => void;
}

export const SalesStatusDialog: React.FC<SalesStatusDialogProps> = ({
    open,
    onClose,
    salesId,
    currentStatus,
    newStatus,
    onSuccess
}) => {
    const { userInfo } = useUser();
    const auth = useAuth();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ステータス表示名の取得
    const getStatusLabel = (status: SalesStatus): string => {
        switch (status) {
            case SalesStatus.DRAFT:
                return '下書き';
            case SalesStatus.READY:
                return '準備完了';
            case SalesStatus.CONFIRMED:
                return '確定済み';
            default:
                return status;
        }
    };

    // ステータス変更の実行
    const handleUpdateStatus = async () => {
        // LastName プロパティを使用
        if (!userInfo?.lastName) {
            setError('ユーザー情報が取得できません。再ログインしてください。');
            return;
        }

        // 経理担当者の権限チェック（フロントエンド側で実施）
        if (userInfo.user_type !== 'clerk') {
            setError('経理担当者としての権限がありません。');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            // ステータス更新APIの呼び出し - LastName プロパティを使用
            const token = auth.user?.access_token;
            await salesService.updateStatus(salesId, {
                status: newStatus,
                userLastName: userInfo.lastName
            }, token);

            // 成功コールバックの呼び出し
            onSuccess();
            handleClose();
        } catch (err: any) {
            console.error('売上ステータスの更新に失敗しました:', err);
            setError(err.message || '売上ステータスの更新に失敗しました');
        } finally {
            setLoading(false);
        }
    };


    // ダイアログを閉じる
    const handleClose = () => {
        if (!loading) {
            setError(null);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.level1'
            }}>
                売上ステータス変更
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 3 }}>
                <Typography variant="body1" paragraph>
                    以下のステータス変更を行います:
                </Typography>

                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'background.level2',
                    p: 2,
                    borderRadius: 1,
                    mb: 2
                }}>
                    <Box>
                        <Typography variant="body2" color="textSecondary">現在:</Typography>
                        <Typography variant="body1" fontWeight="medium">
                            {getStatusLabel(currentStatus)}
                        </Typography>
                    </Box>

                    <Box sx={{ fontSize: 24, color: 'text.secondary' }}>→</Box>

                    <Box>
                        <Typography variant="body2" color="textSecondary">変更後:</Typography>
                        <Typography variant="body1" fontWeight="medium" color="primary.main">
                            {getStatusLabel(newStatus)}
                        </Typography>
                    </Box>
                </Box>

                {newStatus === SalesStatus.CONFIRMED && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        「確定済み」に変更すると、月次レポートに反映されます。
                    </Alert>
                )}

                {(currentStatus === SalesStatus.CONFIRMED && newStatus !== SalesStatus.CONFIRMED) && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        「確定済み」から変更すると、一時的に月次レポートから除外されます。
                    </Alert>
                )}

                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{
                borderTop: 1,
                borderColor: 'divider',
                px: 3,
                py: 2
            }}>
                <Button
                    onClick={handleClose}
                    color="inherit"
                    disabled={loading}
                >
                    キャンセル
                </Button>
                <Button
                    onClick={handleUpdateStatus}
                    color="primary"
                    variant="contained"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : undefined}
                >
                    {loading ? '更新中...' : '変更する'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};